import { Box, Chip, Tooltip, Typography } from '@mui/material';
import DrilldownDialog from './DrilldownDialog';
import { STUDENT_KIND_LABEL, mauStudentsUrl } from './mauStudents';
import { Date1, Mobile, digits } from './drilldownCells';

// The popup behind a CRO's count on the MAU page: the students that make up the number -
//   new students    = whose first payment fell in the month
//   active students = enrolled by the end of the month and active at that date
// who they are (name, admission number, mobile), the course(s) and program, when they enrolled / first paid, who enrolled
// them and which CRO handles them. One row per student, so the rows add up to the number that was clicked.
const uniquePrograms = (r) => [...new Set((r.courses || []).map((c) => c.program).filter(Boolean))];

const Courses = ({ row }) => {
  const names = (row.courses || []).map((c) => c.course).filter(Boolean);
  if (!names.length) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const shown = names.slice(0, 2);
  const rest = names.slice(2);
  return (
    <Box>
      {shown.map((n, i) => <Typography key={i} variant="body2" fontWeight={i === 0 ? 600 : 400}>{n}</Typography>)}
      {rest.length > 0 && (
        <Tooltip title={rest.join('\n')}><Chip size="small" label={`+${rest.length} more`} sx={{ mt: 0.25 }} /></Tooltip>
      )}
    </Box>
  );
};

const COLUMNS = (kind) => [
  { key: 'name', label: 'Student', sort: (r) => (r.name || '').toLowerCase(), render: (r) => <Typography variant="body2" fontWeight={600}>{r.name || '-'}</Typography>, csv: (r) => r.name },
  { key: 'admnNo', label: 'Admn no', sort: (r) => String(r.admnNo || ''), render: (r) => <Typography variant="body2">{r.admnNo || '-'}</Typography>, csv: (r) => r.admnNo },
  { key: 'mobile', label: 'Mobile', sort: (r) => digits(r.mobile), render: (r) => <Mobile value={r.mobile} />, csv: (r) => r.mobile },
  { key: 'course', label: kind === 'new' ? 'Course' : 'Course(s)', sort: (r) => ((r.courses || [])[0]?.course || '').toLowerCase(), render: (r) => <Courses row={r} />, csv: (r) => (r.courses || []).map((c) => c.course).filter(Boolean).join(' / ') },
  { key: 'program', label: 'Program', sort: (r) => (uniquePrograms(r)[0] || '').toLowerCase(), render: (r) => <Typography variant="body2">{uniquePrograms(r).join(', ') || '-'}</Typography>, csv: (r) => uniquePrograms(r).join(' / ') },
  ...(kind === 'new'
    ? [{ key: 'firstPaidOn', label: 'First payment', sort: (r) => r.firstPaidOn || '', render: (r) => <Date1 value={r.firstPaidOn} dateOnly />, csv: (r) => r.firstPaidOn }]
    : []),
  { key: 'enrolledOn', label: 'Enrolled on', sort: (r) => r.enrolledOn || '', render: (r) => <Date1 value={r.enrolledOn} dateOnly />, csv: (r) => r.enrolledOn },
  { key: 'enrolledBy', label: 'Enrolled by', sort: (r) => (r.enrolledBy || '').toLowerCase(), render: (r) => <Typography variant="body2">{r.enrolledBy || '-'}</Typography>, csv: (r) => r.enrolledBy },
  { key: 'cro', label: 'CRO', sort: (r) => (r.cro || '').toLowerCase(), render: (r) => <Typography variant="body2">{r.cro || '-'}</Typography>, csv: (r) => r.cro },
];

const searchText = (r) => [r.name, r.admnNo, digits(r.mobile), r.mobile, r.enrolledBy, ...(r.courses || []).flatMap((c) => [c.course, c.program])]
  .filter(Boolean).join(' ').toLowerCase();

const StudentListDialog = ({ request, onClose }) => {
  const { kind, userName, month } = request;
  return (
    <DrilldownDialog
      url={mauStudentsUrl(request)}
      title={STUDENT_KIND_LABEL[kind]}
      chips={[userName, month.format('MMMM YYYY')]}
      columns={COLUMNS(kind)}
      searchText={searchText}
      searchLabel="Search these students"
      searchPlaceholder="Search student, admn no, mobile, course"
      emptyText="No students."
      rowKey={(r) => r.admnNo}
      csvName={`${STUDENT_KIND_LABEL[kind].replace(/ /g, '_')}_${(userName || 'CRO').replace(/\W+/g, '_')}_${month.format('YYYY-MM')}.csv`}
      onClose={onClose}
    />
  );
};

export default StudentListDialog;
